import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeTooltipPosition } from './tooltipPosition';
import { toolbarTooltipCss } from './toolbarTooltipDom';
/** Copy confirmation appears as soon as the write succeeds, including on touch. */
export function CopyTooltip({ label, copied, failed = false, children }) {
    const id = useId();
    const host = useRef(null);
    const bubble = useRef(null);
    const timer = useRef();
    const [hovered, setHovered] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [position, setPosition] = useState(null);
    const feedback = copied || failed;
    const visible = !dismissed && (feedback || hovered);
    const text = copied ? 'Copied!' : failed ? 'Copy failed' : label;
    const root = host.current?.getRootNode();
    const portal = host.current?.closest('dialog') ?? (root && root.nodeType === 11
        ? root.querySelector('#overlayqa-overlay-container') ?? root
        : host.current?.ownerDocument.body);
    useEffect(() => { setDismissed(false); }, [copied, failed]);
    useEffect(() => () => clearTimeout(timer.current), []);
    const hide = () => {
        clearTimeout(timer.current);
        setHovered(false);
    };
    const show = () => {
        clearTimeout(timer.current);
        setDismissed(false);
        timer.current = setTimeout(() => setHovered(true), 400);
    };
    useLayoutEffect(() => {
        const trigger = host.current?.firstElementChild;
        const tip = bubble.current;
        if (!visible || !trigger || !tip) {
            setPosition(null);
            return;
        }
        const win = trigger.ownerDocument.defaultView;
        let frame;
        // A recovery field can move the trigger without resizing it or emitting a scroll.
        // Track layout only while the tooltip is visible; unchanged geometry does not render.
        const update = () => {
            const next = computeTooltipPosition({
                trigger: trigger.getBoundingClientRect(), tooltip: tip.getBoundingClientRect(),
                placement: 'top', viewport: { width: win.innerWidth, height: win.innerHeight },
            });
            setPosition(previous => previous?.top === next.top && previous.left === next.left && previous.placement === next.placement ? previous : next);
            frame = win.requestAnimationFrame(update);
        };
        update();
        return () => win.cancelAnimationFrame(frame);
    }, [visible, text]);
    useEffect(() => {
        if (!visible)
            return;
        const win = host.current?.ownerDocument.defaultView;
        if (!win)
            return;
        const dismiss = () => { hide(); setDismissed(true); };
        const stackEscape = (event) => { event.preventDefault(); dismiss(); };
        const key = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                dismiss();
            }
        };
        win.addEventListener('keydown', key, true);
        win.addEventListener('oq:copy-tooltip-escape', stackEscape);
        win.addEventListener('scroll', dismiss, true);
        // Scroll events inside the extension's shadow root do not reach window.
        const shadow = host.current?.getRootNode();
        if (shadow?.nodeType === 11)
            shadow.addEventListener('scroll', dismiss, true);
        win.addEventListener('resize', dismiss);
        return () => {
            win.removeEventListener('keydown', key, true);
            win.removeEventListener('oq:copy-tooltip-escape', stackEscape);
            win.removeEventListener('scroll', dismiss, true);
            if (shadow?.nodeType === 11)
                shadow.removeEventListener('scroll', dismiss, true);
            win.removeEventListener('resize', dismiss);
        };
    }, [visible]);
    return _jsxs(_Fragment, { children: [_jsx("span", { ref: host, style: { display: 'contents' }, onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide, children: cloneElement(children, {
                    title: undefined,
                    'aria-describedby': visible ? [children.props['aria-describedby'], id].filter(Boolean).join(' ') : children.props['aria-describedby'],
                }) }), visible && portal && createPortal(_jsxs(_Fragment, { children: [_jsx("style", { children: toolbarTooltipCss }), _jsxs("div", { ref: bubble, id: id, role: "tooltip", "aria-live": feedback ? 'polite' : undefined, className: "oq-capture-tooltip", "data-visible": position ? '' : undefined, "data-placement": position?.placement ?? 'top', style: position ? { top: position.top, left: position.left } : { visibility: 'hidden' }, children: [text, _jsx("span", { className: "oq-capture-tooltip__arrow" })] })] }), portal)] });
}
//# sourceMappingURL=CopyTooltip.js.map